/** Map Paygift/API errors to ID UI strings (log raw messages separately).
 * End-user copy must NOT mention vendor/mitra (Paygift/Aldmic).
 */

const STATUS_SEDANG_DIPROSES = 'Sedang Diproses';

const PENDING_HINT =
  'Kupon Anda sudah tersimpan. Voucher sedang diproses. Ketuk ikon segarkan di pojok kanan atas secara berkala untuk mengecek status.';

const PENDING_FOOTER =
  'Jika masih terkendala setelah beberapa saat, hubungi Customer Service Zoolyfe.';

/** Shared claim-success copy when voucher issue is queued / auto-retrying. */
export const paygiftPendingClaimUi = {
  title: 'Kupon tersimpan',
  headerMessage:
    'Voucher sedang diproses. Silakan cek kembali di menu Kupon secara berkala.',
  queueNotice:
    'Kupon Anda sudah tersimpan. Link voucher akan muncul setelah proses selesai. Silakan segarkan secara berkala.',
  usageNote:
    "Silakan buka menu 'Coupon - Shopping Coupon - View Details' secara berkala. Jika masih terkendala, hubungi Customer Service Zoolyfe.",
  statusLabel: STATUS_SEDANG_DIPROSES,
};

/** Shared wait UI for all pending cases (end-user sees one status only). */
const paygiftSharedPendingUi = {
  statusLabel: STATUS_SEDANG_DIPROSES,
  expiryHint: PENDING_HINT,
  expiryHintNoDate: PENDING_HINT,
  footerHint: PENDING_FOOTER,
  resolvedTitle: 'Voucher sudah tersedia',
  resolvedBody: 'Voucher sudah siap digunakan. Silakan gunakan kupon Anda.',
};

const PATTERNS = [
  {
    test: (m) =>
      /sementara tidak dapat diklaim/i.test(m) ||
      /voucher mitra sedang tidak tersedia/i.test(m) ||
      /stok voucher/i.test(m),
    title: 'Voucher belum bisa diklaim',
    body:
      'Voucher ini sementara tidak dapat diklaim. Silakan coba lagi nanti secara berkala.\n\nJika masih terkendala, hubungi Customer Service Zoolyfe.',
  },
  {
    test: (m) =>
      /Missing Paygift transaction id/i.test(m) ||
      /trxid.*orderNo/i.test(m) ||
      /order reference.*orderNo/i.test(m),
    title: 'Voucher sedang diproses',
    body:
      'Data voucher belum lengkap. Ketuk ikon segarkan di pojok kanan atas secara berkala.\n\nJika tetap tidak bisa, hubungi Customer Service Zoolyfe dan sebutkan tanggal klaim atau nama kupon Anda.',
  },
  {
    test: (m) => /voucher url not available/i.test(m) || /Failed to update voucher URL/i.test(m),
    title: 'Link voucher belum tersedia',
    body:
      'Link penggunaan kupon belum bisa ditampilkan. Segarkan halaman secara berkala atau coba lagi nanti.\n\nJika masih terkendala, hubungi Customer Service Zoolyfe.',
  },
  {
    test: (m) => /network request failed/i.test(m) || /Network Error/i.test(m) || /fetch/i.test(m),
    title: 'Koneksi bermasalah',
    body: 'Periksa internet Anda, lalu coba lagi nanti.',
  },
];

/** @param {string} rawMessage */
export function getPaygiftUserFacingCopy(rawMessage) {
  const m = typeof rawMessage === 'string' ? rawMessage.trim() : '';

  for (const p of PATTERNS) {
    if (p.test(m)) {
      return { title: p.title, body: p.body };
    }
  }

  return {
    title: 'Kupon tidak dapat ditampilkan',
    body:
      'Terjadi kendala saat memuat voucher. Silakan segarkan secara berkala atau hubungi Customer Service Zoolyfe jika masalah berlanjut.',
  };
}

/** @param {string|null|undefined} voucherName */
export function getPaygiftResolvedBody(voucherName) {
  const name = typeof voucherName === 'string' ? voucherName.trim() : '';
  const label = name || 'Voucher';
  return `${label} sudah siap digunakan. Silakan gunakan kupon Anda.`;
}

/** @param {string|null|undefined} voucherName */
export function getPaygiftReportSentAndReadyBody(voucherName) {
  const name = typeof voucherName === 'string' ? voucherName.trim() : '';
  const label = name || 'Voucher';
  return `Laporan sudah kami terima. ${label} juga baru saja tersedia — silakan gunakan kupon Anda.`;
}

/** Merchant-pending screen (same end-user copy as other pending cases). */
export const paygiftMerchantPendingUi = { ...paygiftSharedPendingUi };

/** Quantity shortage pending — reason kept for logic; UI same as shared. */
export const paygiftStockShortageUi = { ...paygiftSharedPendingUi };

export const PAYGIFT_PENDING_REASON_QUANTITY_SHORTAGE = 'quantity_shortage';
export const PAYGIFT_PENDING_REASON_NOT_AVAILABLE = 'not_available_sales_product';
export const PAYGIFT_PENDING_REASON_API_DOWN = 'api_down_maintenance';
export const PAYGIFT_PENDING_REASON_NOT_SALES_PERIOD = 'not_sales_period';
export const PAYGIFT_PENDING_REASON_INSUFFICIENT_DEPOSIT = 'insufficient_deposit';
export const PAYGIFT_PENDING_REASON_DUPLICATED_PURCHASE = 'duplicated_purchase';

function getPendingReason(coupon) {
  if (!coupon || typeof coupon !== 'object') return '';
  return String(
    coupon.paygift_pending_reason ||
      coupon.voucher_details?.paygift_pending_reason ||
      ''
  );
}

export function isPaygiftStockShortagePending(coupon) {
  return getPendingReason(coupon) === PAYGIFT_PENDING_REASON_QUANTITY_SHORTAGE;
}

/** Not available (Sales product) pending — reason for logic; UI same as shared. */
export const paygiftNotAvailableUi = { ...paygiftSharedPendingUi };

export function isPaygiftNotAvailablePending(coupon) {
  const reason = getPendingReason(coupon);
  return (
    reason === PAYGIFT_PENDING_REASON_NOT_AVAILABLE ||
    reason === PAYGIFT_PENDING_REASON_NOT_SALES_PERIOD
  );
}

/** API down / maintenance pending — reason for logic; UI same as shared. */
export const paygiftApiDownUi = { ...paygiftSharedPendingUi };

export function isPaygiftApiDownPending(coupon) {
  return getPendingReason(coupon) === PAYGIFT_PENDING_REASON_API_DOWN;
}

/** Deposit / period / duplicated — same end-user wait UI. */
export const paygiftDepositPendingUi = { ...paygiftSharedPendingUi };

export function isPaygiftDepositOrOpsPending(coupon) {
  const reason = getPendingReason(coupon);
  return (
    reason === PAYGIFT_PENDING_REASON_INSUFFICIENT_DEPOSIT ||
    reason === PAYGIFT_PENDING_REASON_DUPLICATED_PURCHASE
  );
}

export function isPaygiftAutoRetryPending(coupon) {
  return (
    isPaygiftStockShortagePending(coupon) ||
    isPaygiftNotAvailablePending(coupon) ||
    isPaygiftApiDownPending(coupon)
  );
}

export function getPaygiftPendingWaitUi(coupon) {
  if (isPaygiftStockShortagePending(coupon)) return paygiftStockShortageUi;
  if (isPaygiftNotAvailablePending(coupon)) return paygiftNotAvailableUi;
  if (isPaygiftApiDownPending(coupon)) return paygiftApiDownUi;
  if (isPaygiftDepositOrOpsPending(coupon)) return paygiftDepositPendingUi;
  return null;
}

/**
 * Claim-success modal copy aligned with detail-screen pending states.
 * @param {object|string|null|undefined} couponOrReason
 */
export function getPaygiftClaimModalUi(couponOrReason) {
  const coupon =
    typeof couponOrReason === 'string'
      ? { paygift_pending_reason: couponOrReason }
      : couponOrReason && typeof couponOrReason === 'object'
        ? couponOrReason
        : {};
  const waitUi = getPaygiftPendingWaitUi(coupon);

  if (waitUi) {
    return {
      title: paygiftPendingClaimUi.title,
      headerMessage: waitUi.expiryHintNoDate || paygiftPendingClaimUi.headerMessage,
      queueNotice: waitUi.footerHint || paygiftPendingClaimUi.queueNotice,
      usageNote: paygiftPendingClaimUi.usageNote,
      statusLabel: waitUi.statusLabel,
    };
  }

  return {
    ...paygiftPendingClaimUi,
    statusLabel: paygiftMerchantPendingUi.statusLabel,
    queueNotice: paygiftMerchantPendingUi.expiryHintNoDate,
    headerMessage: paygiftMerchantPendingUi.expiryHintNoDate,
  };
}

/** Compact label for Shopping Coupon list badge. */
export function getPaygiftListStatusLabel(coupon) {
  if (!isPaygiftClaimPending(coupon)) return null;
  return STATUS_SEDANG_DIPROSES;
}

/** True when claim modal should show queued/retry copy (no usable voucher URL yet). */
export function isPaygiftClaimPending(coupon) {
  if (!coupon || typeof coupon !== 'object') return false;
  const isPaygift =
    !!(coupon.paygift ||
      coupon.isPaygift ||
      coupon.tipe_kupon === 'paygift' ||
      coupon.type === 'paygift');
  if (!isPaygift) return false;
  if (isPaygiftAutoRetryPending(coupon) || isPaygiftDepositOrOpsPending(coupon)) return true;
  const url = coupon.voucher_url;
  return !(url && String(url).trim());
}
